import {
  Badge,
  Button,
  Checkbox,
  Flex,
  InputGroup,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterDateRange, FilterField, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { BulkActionBar, SelectAllCheckbox, SelectRowCheckbox, useRowSelection } from '../../../components/RowSelection'
import { FormSelect } from '../../../components/Select'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { Tooltip } from '../../../components/Tooltip'
import { capitalize, formatDateTime, useHasRole, useAppToast, formatPhone } from '../../../helpers'
import { FormLeadService } from '../../../services/FormLead/FormLeadService'
import { FormLeadCreateModal } from './FormLeadCreateModal'
import { FormLeadsEditModal } from './FormLeadsEditModal'
import { formatOptions, IFormLead, IFormLeadStatus, statusColors, statusOptions, statusTranslations } from './models'

interface IFormLeadsFilters {
  status?: IFormLeadStatus
  format: string[]
  course: string
  consultant: string
  period: { from: string; to: string }
}

const INITIAL_FILTERS: IFormLeadsFilters = {
  format: [],
  course: '',
  consultant: '',
  period: { from: '', to: '' }
}

export default function FormLeadsPage() {
  const service = new FormLeadService()
  const [isDeleting, setIsDeleting] = useState(false)
  const toast = useAppToast()
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [leads, setLeads] = useState<IFormLead[]>([])
  const [length, setLength] = useState(DEFAULT_PAGE_SIZE)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [draw, setDraw] = useState(0)
  const [search, setSearch] = useState('')
  const [modalLead, setModalLead] = useState<IFormLead | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [sellers, setSellers] = useState<string[]>([])
  const [leadsCounting, setLeadsCounting] = useState<{
    loading: boolean
    total: number
    Qualificado: number
    Desqualificado: number
    'Sem Contato': number
  }>({
    loading: true,
    total: 0,
    Qualificado: 0,
    Desqualificado: 0,
    'Sem Contato': 0
  })
  const { isOpen: deleteDialogIsOpen, onOpen: deleteDialogOnOpen, onClose: deleteDialogIsClose } = useDisclosure()
  const {
    isOpen: deleteMultipleDialogIsOpen,
    onOpen: deleteMultipleDialogOnOpen,
    onClose: deleteMultipleDialogIsClose
  } = useDisclosure()
  const [leadToDelete, setLeadToDelete] = useState<IFormLead>()
  const selection = useRowSelection(leads)
  const [filters, setFilters] = useState<IFormLeadsFilters>(INITIAL_FILTERS)
  const isDirty =
    !!filters.status ||
    filters.format.length > 0 ||
    filters.course !== '' ||
    filters.consultant !== '' ||
    filters.period.from !== '' ||
    filters.period.to !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilters = (patch: Partial<IFormLeadsFilters>) => {
    setFilters({ ...filters, ...patch })
    setPage(1)
  }
  const [updatingStatus, setUpdatingStatus] = useState<number[]>([])
  const [isEditingStatus, setIsEditingStatus] = useState<number[]>([])
  const hasRole = useHasRole()
  const permissions = {
    addLead: hasRole('add_lead'),
    editLead: hasRole('edit_lead'),
    deleteLead: hasRole('delete_lead'),
    manageSources: hasRole('manage_lead_sources')
  }

  const loadSellers = async () => {
    setSellers(await service.getSellers())
  }

  // A mensagem da falha é do `loadLeads`, que roda junto: dois toasts para a mesma queda de
  // rede seriam ruído. O que não pode é a contagem ficar carregando para sempre.
  const loadQuantities = async () => {
    setLeadsCounting({ ...leadsCounting, loading: true })

    try {
      const quantities = await service.getQuantities({
        consultant: filters.consultant,
        course: filters.course,
        format: filters.format,
        period: {
          from: filters.period.from,
          to: filters.period.to
        },
        search: search,
        status: filters.status
      })

      setLeadsCounting({ ...quantities, loading: false })
    } catch {
      setLeadsCounting((counting) => ({ ...counting, loading: false }))
    }
  }

  const order: { column: number; dir: string } = { column: 0, dir: 'desc' }

  const handleDelete = async () => {
    if (!leadToDelete) {
      return
    }

    setIsDeleting(true)
    try {
      await service.delete(leadToDelete.id)
      toast.success('Lead excluído')
      deleteDialogIsClose()
      loadLeads()
    } catch (error) {
      toast.error('Erro ao excluir lead', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteSelected = async () => {
    setIsDeleting(true)
    try {
      await service.deleteMultiple(selection.selectedIds)
      toast.success('Leads excluídos')
      deleteMultipleDialogIsClose()
      loadLeads()
    } catch (error) {
      toast.error('Erro ao excluir os leads', error)
    } finally {
      setIsDeleting(false)
    }
  }

  const loadLeads = async () => {
    setIsLoading(true)
    setLoadError(false)
    loadQuantities()

    try {
      const leadsList = await service.list({
        draw,
        length,
        start: (page - 1) * length,
        order: [order],
        search: { value: search },
        status: filters.status,
        format: filters.format,
        course: filters.course,
        consultant: filters.consultant,
        period: { from: filters.period?.from || '', to: filters.period?.to || '' }
      })

      setDraw(draw + 1)

      setTotalPages(Math.ceil(leadsList.recordsFiltered / length))

      setLeads(leadsList.data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os leads', error)
    } finally {
      setIsLoading(false)
    }
  }

  // `search` chega debounced do `FilterSearch`, então isto não dispara mais uma requisição por tecla.
  useEffect(() => {
    loadLeads()
  }, [page, search, length, filters])

  useEffect(() => {
    loadSellers()
  }, [])

  const handleOpenModal = async (lead: IFormLead) => {
    setModalLead(lead)
    setIsModalOpen(true)
  }

  const handleDeleteDialog = (lead: IFormLead) => {
    deleteDialogOnOpen()
    setLeadToDelete(lead)
  }

  const handleStatusChange = async (newStatus: IFormLeadStatus, id: number) => {
    setUpdatingStatus([...updatingStatus, id])

    await service.updateStatus(id, newStatus)
    loadQuantities()

    const formLeads = [...leads]
    const leadIndex = formLeads.findIndex((lead) => lead.id === id)
    formLeads[leadIndex].status = newStatus

    setLeads(leads)

    setUpdatingStatus(updatingStatus.filter((statusId) => statusId !== id))
    setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== id))
  }

  return (
    <>
      <PageTitle
        title="Base de Leads"
        description="Leads captados pelos formulários do site"
        mb="2"
        actions={
          <div>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Total de leads:{' '}
              {leadsCounting.loading ? <Skeleton w={10} h={4} display={'inline-block'} ms={1} /> : leadsCounting.total}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Qualificados:{' '}
              {leadsCounting.loading ? (
                <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
              ) : (
                leadsCounting.Qualificado || 0
              )}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Desqualificados:{' '}
              {leadsCounting.loading ? (
                <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
              ) : (
                leadsCounting.Desqualificado || 0
              )}
            </Text>
            <Text
              fontSize="md"
              color="gray.500"
              textAlign="right"
              mr="2"
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
            >
              Sem contato:{' '}
              {leadsCounting.loading ? (
                <Skeleton w={10} h={4} display={'inline-block'} ms={1} />
              ) : (
                leadsCounting['Sem Contato'] || 0
              )}
            </Text>
          </div>
        }
      />
      <FilterBar
        onClear={() => {
          updateFilters(INITIAL_FILTERS)
          setSearch('')
        }}
        isDirty={isDirty || search !== ''}
      >
        <FilterSearch
          value={search}
          onChange={(value) => {
            setSearch(value)
            setPage(1)
          }}
          placeholder="Buscar lead"
        />
        <FilterSelect
          label="Status"
          value={filters.status ?? ''}
          onChange={(status) => updateFilters({ status: (status || undefined) as IFormLeadStatus | undefined })}
          options={statusOptions}
        />
        {/* Base maior que a dos vizinhos: com 200px os quatro checkboxes quebram em duas
            linhas, o campo fica mais alto e o rótulo dele sobe para fora da linha da barra. */}
        <FilterField label="Formato" flex="2 1 340px" maxW="none">
          {/* `minH` do tamanho de um controle `sm`: sem isso a linha de checkboxes
              fica mais baixa que os vizinhos e desalinha a barra. */}
          <Flex alignItems="center" minH="32px" gap="2" flexWrap="wrap">
            {formatOptions.map(({ value, label }) => (
              <Checkbox
                key={value}
                isChecked={filters.format.includes(value)}
                onChange={(e) =>
                  updateFilters({
                    format: e.target.checked
                      ? [...filters.format, value]
                      : filters.format.filter((format) => format !== value)
                  })
                }
              >
                {label}
              </Checkbox>
            ))}
          </Flex>
        </FilterField>
        <FilterSelect
          label="Responsável"
          value={filters.consultant}
          onChange={(consultant) => updateFilters({ consultant })}
          options={sellers.map((seller) => ({ value: seller, label: seller }))}
        />
        <FilterDateRange
          label="Data da solicitação"
          value={{ start: filters.period.from, end: filters.period.to }}
          onChange={(date) => updateFilters({ period: { from: date.start, to: date.end } })}
        />
      </FilterBar>

      <BulkActionBar selection={selection} entity="lead">
        <Button
          size="sm"
          colorScheme="red"
          leftIcon={<Icon name="lni lni-trash-can" size="md" />}
          onClick={deleteMultipleDialogOnOpen}
        >
          Excluir
        </Button>
      </BulkActionBar>

      <TableContainer padding="0" mt="5">
        <Table>
          <Thead>
            <Tr>
              <SelectAllCheckbox selection={selection} />
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>E-mail</Th>
              <Th>Telefone</Th>
              <Th>Formato</Th>
              <Th>Curso</Th>
              <Th>Origem</Th>
              <Th>Responsável</Th>
              <Th>Status</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <TableSkeletonRows cols={12} />
            ) : loadError ? (
              <TableErrorRow colSpan={12} entity="leads" onRetry={loadLeads} />
            ) : leads.length === 0 ? (
              <TableEmptyRow colSpan={12} entity="lead" />
            ) : (
              leads.map((lead) => (
                <Tr key={lead.id}>
                  <SelectRowCheckbox selection={selection} id={lead.id} />
                  <Td>{lead.id}</Td>
                  <Td maxW="200px">
                    <Tooltip label={lead.name} isDisabled={!lead.name}>
                      <Text noOfLines={1}>{lead.name}</Text>
                    </Tooltip>
                  </Td>
                  <Td maxW="200px">
                    <Tooltip label={lead.email} isDisabled={!lead.email}>
                      <Text noOfLines={1}>{lead.email}</Text>
                    </Tooltip>
                  </Td>
                  <Td>{formatPhone(lead.phone)}</Td>
                  <Td>{capitalize(lead.format)}</Td>
                  <Td maxW="200px">
                    <Tooltip label={`${lead.course}${lead.area ? ` (${lead.area})` : ''}`}>
                      <Text noOfLines={1}>
                        {lead.course}
                        {lead.area ? ` (${lead.area})` : ''}
                      </Text>
                    </Tooltip>
                  </Td>
                  <Td>{lead.source}</Td>
                  <Td>{lead.seller_name}</Td>
                  {/* 210px: largura mínima para o Select mostrar "Desqualificado" ao lado do botão de cancelar. */}
                  <Td textAlign="center" w="210px">
                    {isEditingStatus.includes(lead.id) ? (
                      <InputGroup display="flex" alignItems="center">
                        <FormSelect
                          size="sm"
                          isDisabled={updatingStatus.includes(lead.id)}
                          value={lead.status}
                          onChange={(value) => handleStatusChange(value as IFormLeadStatus, lead.id)}
                          options={[
                            { value: 'Sem Contato', label: statusTranslations['Sem Contato'] },
                            { value: 'Qualificado', label: statusTranslations.Qualificado },
                            { value: 'Desqualificado', label: statusTranslations.Desqualificado }
                          ]}
                          hideEmptyOption
                        />
                        <ActionButton
                          action="cancel"
                          ml="1"
                          mr="0"
                          onClick={() => setIsEditingStatus(isEditingStatus.filter((statusId) => statusId !== lead.id))}
                        />
                      </InputGroup>
                    ) : (
                      <Tooltip label="Clique duas vezes para editar" aria-label="Clique duas vezes para editar">
                        <Badge
                          colorScheme={statusColors[lead.status]}
                          onDoubleClick={() => setIsEditingStatus([...isEditingStatus, lead.id])}
                        >
                          {statusTranslations[lead.status]}
                        </Badge>
                      </Tooltip>
                    )}
                  </Td>
                  <Td>
                    <Tooltip label={formatDateTime(lead.created_at, { showSeconds: true })}>
                      <Text as="span">{formatDateTime(lead.created_at)}</Text>
                    </Tooltip>
                  </Td>
                  <Td>
                    {permissions.editLead && <ActionButton action="edit" onClick={() => handleOpenModal(lead)} />}
                    {permissions.deleteLead && (
                      <ActionButton action="delete" onClick={() => handleDeleteDialog(lead)} />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      <Paginator
        page={page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setPage(page)
        }}
        pageSize={length}
        onPageSizeChange={(pageSize) => {
          setLength(pageSize)
          setPage(1)
        }}
      />

      <ConfirmDialog
        isOpen={deleteDialogIsOpen}
        onClose={deleteDialogIsClose}
        onConfirm={handleDelete}
        action="delete"
        entity="lead"
        name={leadToDelete?.name}
        isLoading={isDeleting}
      />

      <ConfirmDialog
        isOpen={deleteMultipleDialogIsOpen}
        onClose={deleteMultipleDialogIsClose}
        onConfirm={handleDeleteSelected}
        action="delete"
        count={selection.count}
        entity="leads"
        isLoading={isDeleting}
      />

      {permissions.addLead && <ActionFab label="Registrar lead" onClick={() => setIsCreateModalOpen(true)} />}

      <FormLeadCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onCreated={loadLeads}
        canManageSources={permissions.manageSources}
      />

      <FormLeadsEditModal
        lead={modalLead}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          loadLeads()
        }}
        sellers={sellers}
        canManageSources={permissions.manageSources}
      />
    </>
  )
}
